import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  memo,
} from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import store from "../../../State/Store/store";
import StorageManager from "../../../utils/StorageManager";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME, MUSIC_FADE_MS } from "../../../sounds";
import TransitionDialogController from "./TransitionDialogController";
import { safeInit } from "../../../utilities";
import {
  selectShowTransitionDialog,
  selectFreeSpinsTriggered,
  selectFreeSpinsActive,
} from "../../../State/selectors";
import {
  setFreeSpinsActive,
  // setFreeSpinsEntrySequencePending, // Mad Labs level-box flourish — no ladder here
  resetFreeSpins,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
  setFeatureSpinActive,
  setFeatureSpinComplete,
  setAutoplaySpeedNoSave,
  syncGameState,
  resumeAutoPlay,
  syncFreeRoundsWin,
  applyFreeRoundsSyncedWin,
  incrementFreeRoundsDisplayedWin,
} from "../../../State/actions";
import { isTurboLike } from "../../../utils/speedHelpers";
import { useInitialIdleAnimations } from "../../../Hooks/useLandingAnimations";
import { useFreeSpinBasicInitialIdleAnimations } from "../../../Hooks/useFreeSpinBasicLandingAnimations";
import {
  selectHoldAndWinIntroPending,
  setHoldAndWinActive,
  resetHoldAndWin,
} from "../../../State/Slices/holdAndWinSlice";

/**
 * TransitionDialog — the base <-> bonus scene wipe.
 *
 * Same shell as every other popup: never unmounts, visibility is alpha. It
 * differs in two ways — it takes no input (it plays once and the controller
 * closes it on the animation's own `complete`), and it carries NO dimming
 * overlay.
 *
 * The overlay is gone on purpose. Every other popup fades a black Graphics in
 * behind itself, and this one did too; but a full-screen wipe covers and
 * reveals on its own, so the second layer only delayed the reveal by its own
 * 350ms fade — long enough that the bonus room looked like it started
 * before the board was visible. This component now renders one thing:
 * the container the controller adds its spine to.
 */

// Base game <-> bonus room music handover. The outgoing track fades out when
// the wipe STARTS, the incoming one fades in when it ENDS — so this is not a
// crossfade between them, it is two short independent fades with the wipe in
// between. Kept SHORT: this game moves quickly and a long fade just reads as
// the old room hanging on. Long enough to avoid a click, no longer.
const MUSIC_SWAP_MS = MUSIC_FADE_MS;

const TransitionDialog = () => {
  const dispatch = useDispatch();
  const show = useSelector(selectShowTransitionDialog);
  const freeSpinsTriggered = useSelector(selectFreeSpinsTriggered);
  // The BASE game's snap-clear, populated by useWinController. The bonus board
  // has its own (freeSpinBasicSnapClearAllRef) precisely so this one is not
  // overwritten by whichever win controller rendered last.
  const { snapClearAllRef, freeSpinBasicSnapClearAllRef } = useReelRefs();
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  // The base board's resting idles, for the way back (see leavingFreeSpins).
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  // The free-spins board's idles, for the way in (see enteringFreeSpins).
  const { triggerFreeSpinBasicIdleAnimations } =
    useFreeSpinBasicInitialIdleAnimations();

  const controllerRef = useRef(null);
  // Work the midpoint parks for the END of the wipe — see setCompleteHandler.
  const pendingUnlockRef = useRef(null);
  // The track to bring back when the wipe FINISHES. Captured at the start,
  // because the direction flips at the midpoint — `freeSpinsActive` is in the
  // handler effect's deps, so that effect re-registers halfway through with the
  // direction already reversed. Reading it at the end would fade in the room we
  // just LEFT.
  const musicToRestoreRef = useRef(null);
  // This wipe carries the player INTO Hold & Win. Decided on the frame the
  // wipe starts, like the music: the midpoint flips holdAndWin.active.
  const holdAndWinEntryRef = useRef(false);
  // This wipe carries the player OUT of Hold & Win (its Total Win closed).
  const holdAndWinExitRef = useRef(false);
  const containerRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize } = dimensionsContext;


  // ── The controller's existence has to be a rendered value ──
  //
  // The midpoint handler below is registered by an effect keyed on the
  // DIRECTION flags. On a live trigger `freeSpinsTriggered` flips false->true
  // while the app is running, long after the controller exists, so the effect
  // re-runs and the handler lands.
  //
  // On a RELOAD into a round already in progress it is true before this ever
  // mounts. The effect then runs once, finds controllerRef.current still null,
  // bails — and neither flag ever changes again, so NO handler is registered.
  // The wipe plays, the midpoint fires into nothing, setFreeSpinsActive is
  // never dispatched, and the round sits there with no room, no level and no
  // autoplay. A ref cannot go in a dependency list; this can.
  const [controllerReady, setControllerReady] = useState(false);
  // Built once — it lives for the session.
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new TransitionDialogController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
        );
        setControllerReady(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize]);

  // ── What happens under the cover ──
  //
  // `transition_midpoint` fires at t=0.5s of the 1.03s clip, the frame the
  // two full-screen `cover` rectangles are opaque. Everything that would be
  // ugly to watch happens there and is finished before they lift: the rooms
  // swap, the board swaps with them, and the lock the trigger put on the UI
  // comes off.
  //
  // Registered as a HANDLER rather than done on `show`, because the wipe is
  // one animation and the swap has to land on one specific frame of it — not
  // when the flag was set, and not when the clip ends.
  //
  // Re-registered whenever the direction changes: the same wipe carries the
  // player back out of the round later, and the handler has to know which way
  // it is going.
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller) return;

    // The same wipe carries the player both ways, and the two directions are
    // mutually exclusive: entering requires the round NOT to be active, and
    // leaving requires that it is.
    const enteringFreeSpins = freeSpinsTriggered && !freeSpinsActive;
    const leavingFreeSpins = freeSpinsActive;

    controller.setMidpointHandler(() => {
      // ── Into Hold & Win ──
      //
      // FIRST, before the free-spins directions: H&W won inside free spins
      // has freeSpins.active set, which would otherwise read as LEAVING.
      //
      // Only the room we are leaving is cleared — the base board, or the
      // free-spins board when H&W came from there (the FS round stays
      // suspended underneath, GDD §7). holdAndWin.active then swaps the rooms:
      // the H&W room shows, the one below steps aside.
      //
      // The UI comes back when the wipe ends; the respin round (
      // HoldAndWinControlContext) takes over from there.
      if (holdAndWinEntryRef.current) {
        holdAndWinEntryRef.current = false;
        if (store.getState().freeSpins.active) {
          freeSpinBasicSnapClearAllRef?.current?.();
        } else {
          snapClearAllRef.current?.();
          // No turbo in H&W (the free-spins rule, NoSave).
          if (isTurboLike(store.getState().settings.autoPlaySpeed)) {
            dispatch(setAutoplaySpeedNoSave("auto"));
          }
        }
        dispatch(setHoldAndWinActive(true));
        // The UI back at the END of the wipe, as free spins does — the
        // respin button is live once the room is uncovered.
        pendingUnlockRef.current = () => {
          dispatch(setUserInterfaceDisabled(false));
          dispatch(setCriticalAnimationPlaying(false));
        };
        return;
      }

      // ── Out of Hold & Win ──
      //
      // Also before the free-spins directions, for the same reason as the
      // entry: H&W won inside free spins still has freeSpins.active set.
      //
      // resetHoldAndWin clears `active`, and the rooms read it with opposite
      // signs, so the swap back is atomic — to the base game, or to the free
      // spins round H&W came from (GDD "Outro and Return State"), which then
      // carries on by itself.
      if (holdAndWinExitRef.current) {
        holdAndWinExitRef.current = false;
        const fromFreeSpins = store.getState().freeSpins.active;
        // dispatch(resetHoldAndWin());
        // Back to the base game: its win display keeps the round's TOTAL WIN
        // until the next spin. Back to free spins: their total carries it.
        dispatch(resetHoldAndWin({ keepTotal: !fromFreeSpins }));
        // // The collection has been shown — nothing is owed on a reload now.
        // StorageManager.clearHoldAndWinSettled();
        // (Not here: the record lives until the next play command — RGS
        // spin(), Pot Collection's rule.)
        if (fromFreeSpins) {
          pendingUnlockRef.current = () => {
            dispatch(setUserInterfaceDisabled(false));
            dispatch(setCriticalAnimationPlaying(false));
          };
          return;
        }
        // Back to the base game: the player's own speed (the entry forced
        // auto, NoSave), its board alive again, and the balance — which the
        // round's win has moved — synced under the cover. The UI comes back
        // when the wipe has ended AND the balance is true, as free spins does.
        const savedSpeed =
          StorageManager.loadUISettings()?.autoPlaySpeed || "auto";
        if (store.getState().settings.autoPlaySpeed !== savedSpeed) {
          dispatch(setAutoplaySpeedNoSave(savedSpeed));
        }
        // ── Hand the spin state back to the base game ──
        //
        // Every respin is a FEATURE command, and its response leaves
        // featureSpinActive true (spinSlice). Left so, the next base spin's
        // `pending` reads it and routes that spin to featureSpin* — base
        // isSpinning never flips, the board never starts a new spin and sits
        // on the old animations. The free-spins exit closes both halves for
        // exactly this; the H&W exit did not.
        dispatch(setFeatureSpinActive(false));
        dispatch(setFeatureSpinComplete(true));
        triggerInitialIdleAnimations(false);
        const synced = dispatch(syncGameState({ syncBalance: true }));
        pendingUnlockRef.current = () => {
          synced.finally(() => {
            dispatch(setUserInterfaceDisabled(false));
            dispatch(setCriticalAnimationPlaying(false));
            // Autoplay picks up where the trigger interrupted it — after the
            // unlock and the sync, as the free-spins exit does.
            if (store.getState().settings.autoplayPausedSpins !== null) {
              dispatch(resumeAutoPlay());
            }
          });
        };
        return;
      }

      if (enteringFreeSpins) {
        // ── Strip the base game FIRST, under full cover ──
        //
        // The trigger spin leaves its whole presentation standing: the dim
        // overlay, the win boxes, the looping scatter animations and the hidden
        // reel textures underneath them. Nothing was clearing it, so returning
        // from the bonus put the player back on the trigger win — still dimmed,
        // still celebrating a round that ended minutes ago.
        //
        // snapClearAll is the base game's own teardown and does all of it in one
        // call: kills the win cycling loop, snap-clears the dim, hides the boxes
        // and the line text, stops the overlay animations, and — the part this
        // game needs most — calls showAllSymbolTextures, so the board settles
        // back onto plain static textures.
        //
        // The base board is left on static textures — it is hidden behind the
        // free-spins room until the exit, which starts its idles again. The
        // free-spins board's idles start further down, after the room swap.
        //
        // Done at the MIDPOINT, so the clear happens while the wipe covers the
        // screen and none of it is ever seen.
        snapClearAllRef.current?.();

        // ── Arm the level-box entry flourish ──
        //
        // Everything needed is already known here: freeSpins.serverLevel came
        // off the response, so the number of banked boxes is settled before the
        // room is uncovered. Boxes below the filling one open FULL, convert
        // together once the wipe lifts, and the first spin waits on them.
        //
        // Nothing banked (level 1, an ordinary trigger) means nothing to
        // convert, so this stays false and the round starts as before.
        // Mad Labs only — Cleopatra's Fire has no level ladder (2026-09-29).
        // const fsLevel = store.getState().freeSpins.serverLevel ?? 1;
        // if (fsLevel > 1) {
        //   dispatch(setFreeSpinsEntrySequencePending(true));
        // }

        // ── The bonus runs at ONE speed, and it is auto ──
        //
        // Same rule and the same mechanism Pot Collection uses at its own
        // midpoint (see GameView's bonus entry): the free-spins room has no
        // turbo, so a player who entered on turbo or super turbo would carry a
        // drop time authored for the base game into a round full of trails,
        // conversions and popups that are timed against auto.
        //
        // NoSave, deliberately. The player's turbo preference is a persisted
        // setting, and this is a temporary override for the duration of the
        // round — writing it would silently reset their toolbar when the bonus
        // ended. GameView restores the saved value on the way out.
        //
        // At the MIDPOINT, under the cover, so the play button never visibly
        // changes speed in front of the player.
        //
        // The spacebar HOLD is already gone by now — ReelControlContext's eject
        // effect drops it the moment any feature triggers, which is before the
        // wipe starts. This is the speed setting itself, which that eject
        // deliberately leaves alone.
        if (isTurboLike(store.getState().settings.autoPlaySpeed)) {
          dispatch(setAutoplaySpeedNoSave("auto"));
        }

        // The rooms read this with opposite signs, so the swap is atomic.
        dispatch(setFreeSpinsActive(true));
        // ── Take the round OUT of the base game's spin state ──
        //
        // spinSlice routes rgsPlay.pending on this flag: while it is set, a
        // spin updates featureSpin* and leaves isSpinning / spinComplete
        // alone. It has to be raised HERE rather than left to the first
        // response, because `pending` runs before `fulfilled` — so the first
        // free spin would otherwise flip the base state on its way out.
        // Pot Collection relies on the same flag for its respins.
        dispatch(setFeatureSpinActive(true));
        // ── The trigger board is alive BEFORE the cover lifts ──
        //
        // Cleopatra's symbols have idles (Mad Labs' had none, so its room
        // opened on static textures — see the snapClearAll note above). The
        // board itself is only mounted by the `active` flip just above, so the
        // pass waits for its controllers frame by frame — a frame or two, still
        // behind the wipe — and the player is uncovered onto a board that is
        // already moving (Vladimir, 2026-09-29: "whatever we do we do behind
        // the CURTAINS"). The first spin clears them as any spin does.
        triggerFreeSpinBasicIdleAnimations();
        // ── Enabled at the END, not here ──
        //
        // These two used to fire at the midpoint so the free-spin play button
        // was already live the moment the cover lifted. It is deferred now:
        // there is no point in a live button or a released lock behind a cover
        // that still hides the board, and once the dimming overlay was removed
        // nothing was absorbing a mid-wipe tap either.
        //
        // The cost is a possible ONE FRAME of disabled toolbar — the dispatch
        // lands in the same tick that hides the wipe, so React repaints the
        // button a frame behind the reveal. Nothing needs that button on the
        // first frame: the free-spin loop drives the spins itself, and it is
        // gated on this same transition.
        pendingUnlockRef.current = () => {
          dispatch(setUserInterfaceDisabled(false));
          dispatch(setCriticalAnimationPlaying(false));
          // Moved to the midpoint, under the cover (see above) — started here
          // they switched on in front of the player.
          // triggerFreeSpinBasicIdleAnimations();
        };
        return;
      }

      if (leavingFreeSpins) {
        // ── Give the player their speed back ──
        //
        // The mirror of the override on the way in. The bonus forced `auto`
        // with the NoSave variant, so localStorage still holds whatever the
        // player actually chose — read it back and restore it under the cover,
        // exactly as GameView does at the far side of the Pot Collection wipe.
        //
        // Without this the override outlives the round: a player who entered
        // on super turbo returns to a base game silently running at auto, with
        // the toolbar button still lit.
        const savedSpeed =
          StorageManager.loadUISettings()?.autoPlaySpeed || "auto";
        if (store.getState().settings.autoPlaySpeed !== savedSpeed) {
          dispatch(setAutoplaySpeedNoSave(savedSpeed));
        }

        // ── Back to the base game ──
        //
        // ONE action does the whole teardown. resetFreeSpins returns the slice
        // to its initial state, which clears `active` — and the two rooms read
        // that with opposite signs, so the swap back is atomic. It also drops
        // the free-spin round, the ladder, the counters and the collector, so
        // a second bonus in the same session cannot inherit the first one's
        // progress. That stale-state trap is what the Zeus free-spin flow hit.
        //
        // The base board is untouched and still showing the screen the trigger
        // spin landed on, because the free-spin round was kept in its own
        // slice rather than overwriting game.currentRound.
        // ── The balance, once, here ──
        //
        // Nothing moved it for the whole round: the total win row counted up
        // per spin, and both WinDisplay and WinAnimationSecondaryController
        // deliberately skip the balance while the bonus owns the screen. This
        // is the far side of the wipe, so the player never sees the number
        // change — the same place GameView syncs it for Pot Collection.
        //
        // Before resetFreeSpins, because that clears the slice this reads.
        //
        // ── The UI stays locked until the number lands ──
        //
        // syncGameState is a thunk, not a plain action. Releasing the two
        // flags in this same tick let ButtonPlay re-mount — resetFreeSpins
        // clears `active`, and ToolbarBottom swaps the base button straight
        // back in — with the OLD balance still on screen and a spin one press
        // away. PotCollectionControlContext re-locks at its own midpoint for
        // exactly this reason and releases only after its refresh resolves.
        //
        // The release now hangs off the settle, and `finally` rather than
        // `then`: a failed sync must not leave the player looking at a dead
        // toolbar. The balance is wrong for a moment either way, but the game
        // stays playable and the next response corrects it.
        //
        // ── And off the wipe as well: whichever finishes LAST releases ──
        //
        // The sync was the only gate here; the end of the wipe is now a second
        // one. The handler below runs on the clip's last frame and waits on the
        // sync from there, so the UI comes back when the screen is clear AND
        // the balance is true, never on one without the other. A sync that had
        // already resolved makes it immediate.
        // ── ...unless a free-rounds CAMPAIGN is running ──
        //
        // Then the balance does not move here at all: free-rounds winnings,
        // a bonus won inside them included, settle ONCE at the campaign's end
        // through useFreeRoundsCompletion. This is Pot Collection's own exit
        // rule (GameView, the cloud transition's COMPLETE handler), ported —
        // crediting here "would pay the bonus out a round early".
        //
        // What DOES have to catch up is the campaign's Total Win. The server
        // adds the room's total to `balance.free_rounds` when the round closes
        // (20 -> 65 on the 2026-09-24 trace), but nothing asked for it, so the
        // row went back to the pre-bonus figure and only caught up on the next
        // spin. Vladimir: *"when we have exit transition, we just have to sync
        // the total win, we dont touch the balance."*
        //
        // Two shapes, both Pot Collection's (WinDisplay, its outro block):
        //
        //   mid-campaign   ask the server — syncFreeRoundsWin stages it,
        //                  applyFreeRoundsSyncedWin puts it on screen.
        //   LAST round     the server has already closed the campaign and its
        //                  answer would be stale, so the room's total is added
        //                  by hand and persisted for the completion popup.
        const exitState = store.getState();
        const fr = exitState.freeRounds;
        const frCampaignRunning = fr.status === "active" || fr.completionPending;
        let synced;
        if (frCampaignRunning) {
          const isLastFRRound = fr.count > 0 && fr.performed >= fr.count;
          // A replay never asks the live server (it would fetch the player's
          // real campaign, not the recorded one) and never writes free-rounds
          // storage — both exactly as Pot Collection's outro block rules.
          const replaying = !!exitState.replay?.sessionActive;
          if (isLastFRRound || replaying) {
            // The WHOLE free-spins total, trigger win included. `totalWin` is
            // seeded with the trigger spin's win, and in a campaign that win is
            // deliberately NOT counted at the trigger — it rides into the room
            // and arrives here with the rest (see the `!freeSpinsTriggered`
            // gate in WinDisplay). So this is the only place it is added.
            const roomTotal = exitState.freeSpins.totalWin || 0;
            if (roomTotal > 0) {
              dispatch(incrementFreeRoundsDisplayedWin(roomTotal));
              if (!replaying) {
                StorageManager.updateFreeRoundsWin(
                  (fr.displayedWin || 0) + roomTotal,
                );
              }
            }
            synced = Promise.resolve();
          } else {
            // A rejected sync leaves nothing staged, so the apply is a no-op
            // and the row keeps what it had — never a wrong number.
            synced = dispatch(syncFreeRoundsWin()).then(() =>
              dispatch(applyFreeRoundsSyncedWin()),
            );
          }
        } else {
          synced = dispatch(syncGameState({ syncBalance: true }));
        }
        pendingUnlockRef.current = () => {
          synced.finally(() => {
            dispatch(setUserInterfaceDisabled(false));
            dispatch(setCriticalAnimationPlaying(false));

            // ── Autoplay picks up where the trigger interrupted it ──
            //
            // Ported from Pot Collection's own outro (GameView, the cloud
            // transition's COMPLETE handler), which has always done this. The
            // free-spins room never did: `pauseAutoPlayForFeature` parks the
            // remaining spins in `autoplayPausedSpins` when the feature
            // triggers, and the only three callers of `resumeAutoPlay` are
            // Hold & Win's overlay, Royal Collect's, and the legacy Zeus
            // GameView — all three dead in this game, so the spins were
            // stranded and autoplay simply ended at the trigger.
            //
            // AFTER the unlock and AFTER the balance sync, in that order, for
            // the same reason Pot Collection puts it there: resuming first
            // would queue a spin against a stale balance and a locked UI.
            //
            // Null means nothing was paused — a player who was not on autoplay
            // when the round triggered, or whose autoplay was stopped for good
            // by the responsible-gambling bonus-game rule, which clears this
            // deliberately (see ReelControlContext's clearAutoplayPausedSpins).
            // Either way there is nothing to resume.
            if (store.getState().settings.autoplayPausedSpins !== null) {
              dispatch(resumeAutoPlay());
            }
          });
        };
        // ── THE ROUND IS OVER. Clear its saved record too. ──
        //
        // The record outlives the round for exactly ONE reason: the outro is
        // still owed. The server stops describing a closed round, so a reload
        // during the Total Win has nothing else to rebuild it from.
        //
        // ── REDUX ends here. localStorage does NOT. ──
        //
        // Two different lifetimes, and a clear was added here that confused
        // them:
        //
        //   Redux          the round is over, so the state goes now
        //   the record     lives until the player's next BASE response —
        //                  a plain spin or a buy, both cleared together in
        //                  PopiPlay/RGS where the response lands
        //
        // Until that spin the record still says `closed: true`, and that is
        // deliberate: a reload before it returns the player to the outro, every
        // time. That is the designed behaviour — the round is not finished
        // with until the player chooses to move on.
        //
        // Clearing it here took the record with the Redux state, so a player
        // who walked back to base and reloaded lost the round outright. RGS
        // states the rule where it loads the record: "The record outlives the
        // round on purpose — it owes the outro, and the Total Win row reads it
        // until the player spins the base game again."
        dispatch(resetFreeSpins());
        // Hand the spin state back to the base game, or its next spin would
        // still be routed to featureSpin* and the play button would never
        // see isSpinning.
        //
        // BOTH halves of the shared pair, which is what every other feature
        // does — Pot Collection, Hold and Win, Royal Collect and both error
        // recovery paths all close featureSpinComplete on the way out. Free
        // spins raised featureSpinActive on entry and never closed the other
        // one, so it stayed false from the last free spin until the next base
        // spin happened to repair it, and anything reading it in between was
        // told a spin was still running.
        dispatch(setFeatureSpinActive(false));
        dispatch(setFeatureSpinComplete(true));
        // ── The base board comes back ALIVE ──
        //
        // It was left on static textures at the entry (snapClearAll above).
        // Mad Labs stopped there — its symbols had no idles, so the texture WAS
        // the resting state. Cleopatra's symbols do, so they are started here,
        // under the cover, exactly as the board starts on load (the "idle on
        // load" pass: idle spine on, texture off a frame later). Zeus does the
        // same after its free-spins wipe.
        triggerInitialIdleAnimations(false);
        // The two release dispatches that used to sit here now hang off the
        // sync above.
      }
    });

    // Runs whatever the midpoint parked, exactly once — so a wipe that closes
    // twice (the `complete` event, then the safety timeout standing in for it)
    // cannot re-enable anything a second time.
    controller.setCompleteHandler(() => {
      // ── The wipe is gone: bring the new room's music up ──
      //
      // At the END, not the midpoint. The outgoing track was faded out when
      // the wipe STARTED, so the transition itself plays over near-silence and
      // the new room arrives with its own music rather than inheriting the old
      // one. fadeIn() also records it as the current background music and
      // starts its paired ambient layer silently, so the idle crossfade keeps
      // working in whichever room we landed in.
      const restore = musicToRestoreRef.current;
      musicToRestoreRef.current = null;
      if (restore) soundManager.fadeIn(restore, MUSIC_SWAP_MS);

      const unlock = pendingUnlockRef.current;
      pendingUnlockRef.current = null;
      unlock?.();
    });

    return () => {
      controller.setMidpointHandler(null);
      controller.setCompleteHandler(null);
    };
  }, [controllerReady, dispatch, freeSpinsTriggered, freeSpinsActive, snapClearAllRef, freeSpinBasicSnapClearAllRef, triggerInitialIdleAnimations, triggerFreeSpinBasicIdleAnimations]);

  useEffect(() => {
    if (!controllerRef.current || !show) return;

    // ── Direction is decided HERE, once, on the frame the wipe starts ──
    //
    // Same test the handler effect makes: entering needs the round NOT to be
    // active yet, leaving needs that it is. Captured now because the midpoint
    // flips `freeSpinsActive` and everything after it reads the other way.
    //
    // Hold & Win first: from the base game the feature music takes over; from
    // free spins it is already playing, so nothing changes.
    const st = store.getState();
    // Out of Hold & Win: back to the base game's music; to free spins, the
    // feature music simply keeps playing.
    if (st.holdAndWin.active && st.holdAndWin.phase === "settled") {
      holdAndWinExitRef.current = true;
      if (st.freeSpins.active) {
        musicToRestoreRef.current = null;
      } else {
        soundManager.fadeOut(SOUND_NAME.feature_background_music, MUSIC_SWAP_MS);
        musicToRestoreRef.current = SOUND_NAME.background_music;
      }
      controllerRef.current.startSequence();
      return;
    }
    if (selectHoldAndWinIntroPending(st) && !st.holdAndWin.active) {
      holdAndWinEntryRef.current = true;
      if (st.freeSpins.active) {
        musicToRestoreRef.current = null;
      } else {
        soundManager.fadeOut(SOUND_NAME.background_music, MUSIC_SWAP_MS);
        musicToRestoreRef.current = SOUND_NAME.feature_background_music;
      }
      controllerRef.current.startSequence();
      return;
    }
    const entering = freeSpinsTriggered && !freeSpinsActive;
    const outgoing = entering
      ? SOUND_NAME.background_music
      : SOUND_NAME.feature_background_music;
    musicToRestoreRef.current = entering
      ? SOUND_NAME.feature_background_music
      : SOUND_NAME.background_music;

    // Out IMMEDIATELY, so the wipe is not played over the old room's music.
    soundManager.fadeOut(outgoing, MUSIC_SWAP_MS);

    controllerRef.current.startSequence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="transition_dialog"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      // 10, not 9. Every free-spin dialog sits at 9, so the wipe used to TIE
      // with all of them and won only because Dialogs.jsx happens to mount it
      // last — reorder that JSX and the wipe silently draws underneath the
      // popups it is supposed to cover. This makes today's order explicit
      // rather than accidental. It is free-spins only (the intro dialog and
      // the total win dialog are its two callers), so it cannot collide with
      // Pot Collection's own 10s.
      zIndex={10}
      ref={containerRef}
      eventMode={show ? "auto" : "none"}
    />
  );
};

export default memo(TransitionDialog);
